import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import GallerySidebar from './GallerySidebar';
import GalleryHeader from './GalleryHeader';
import PhotosPanel from './panels/PhotosPanel';
import UploadPanel from './panels/UploadPanel';
import HeadshotDeliveriesPanel from './panels/HeadshotDeliveriesPanel';
import ShareLinksPanel from './panels/ShareLinksPanel';
import AnalyticsPanel from './panels/AnalyticsPanel';
import { viewerTimeZone } from './analytics/analyticsFormat';
import SettingsPanel from './panels/SettingsPanel';
import CollectionsPanel from './panels/CollectionsPanel';
import DeliveriesPanel from './panels/DeliveriesPanel';
import FramesPanel from './panels/FramesPanel';
import { FloatingUploadProgress } from '../FloatingUploadProgress';

const GalleryAdmin = ({ axiosConfig, backendUrl, galleries, userRole = 'admin' }) => {
  const { galleryId } = useParams();
  const navigate = useNavigate();
  const isCrew = userRole === 'crew';

  // Current panel state
  const [activePanel, setActivePanel] = useState('photos');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Close mobile menu when panel changes
  const handlePanelChange = (panel) => {
    setActivePanel(panel);
    setMobileMenuOpen(false);
  };

  // Gallery data
  const [gallery, setGallery] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(0);
  const PHOTOS_PER_PAGE = 50;

  // Tags data
  const [allTags, setAllTags] = useState([]);
  const [categorizedTags, setCategorizedTags] = useState({
    facial_attributes: [],
    emotions: [],
    activities: [],
    people_groups: [],
    event_scenes: [],
    event_objects: [],
    atmosphere: [],
    other: []
  });

  // Analytics data
  const [analytics, setAnalytics] = useState({ total_photos: 0 });

  // Processing status
  const [processingStatus, setProcessingStatus] = useState({
    isProcessing: false,
    fullSizeComplete: false,
    socialSizeComplete: false
  });

  // Initialize gallery data
  useEffect(() => {
    const currentGallery = galleries.find(g => g.id === galleryId);
    if (currentGallery) {
      setGallery(currentGallery);
      checkProcessingStatus(currentGallery);
    }
    fetchPhotos();
    // Tag search and analytics are admin-only endpoints; crew aren't permitted them.
    if (!isCrew) {
      fetchAllTags();
      fetchAnalytics();
    }
  }, [galleryId, galleries]);

  const checkProcessingStatus = (galleryData) => {
    if (!galleryData?.finalized) {
      setProcessingStatus({
        isProcessing: false,
        fullSizeComplete: false,
        socialSizeComplete: false
      });
      return;
    }

    const fullSizeReady = !!galleryData.full_size_zip_url;
    const socialSizeReady = !!galleryData.social_size_zip_url;
    const bothComplete = fullSizeReady && socialSizeReady;

    setProcessingStatus({
      isProcessing: !bothComplete,
      fullSizeComplete: fullSizeReady,
      socialSizeComplete: socialSizeReady
    });
  };

  const [collectionFilter, setCollectionFilter] = useState('all');

  const fetchPhotos = useCallback(async (reset = true, collection = null) => {
    try {
      if (reset) {
        setLoading(true);
        setPage(0);
        setHasMore(true);
      }

      const coll = collection !== null ? collection : collectionFilter;
      const collParam = coll && coll !== 'all' ? `&collection=${coll}` : '';
      const response = await axios.get(
        `${backendUrl}/api/galleries/${galleryId}/photos?limit=${PHOTOS_PER_PAGE}&skip=${reset ? 0 : page * PHOTOS_PER_PAGE}${collParam}`,
        axiosConfig
      );

      if (reset) {
        setPhotos(response.data);
      } else {
        setPhotos(prev => [...prev, ...response.data]);
      }

      setHasMore(response.data.length === PHOTOS_PER_PAGE);
    } catch (error) {
      console.error('Error fetching photos:', error);
      toast.error('Failed to load photos');
    } finally {
      setLoading(false);
    }
  }, [galleryId, backendUrl, axiosConfig, page, collectionFilter]);

  // Keep a ref to the latest fetchPhotos so the tab-switch effect below can call
  // it without depending on its identity (which changes when page/filter change,
  // and would otherwise re-trigger a full refetch during pagination).
  const fetchPhotosRef = useRef(fetchPhotos);
  useEffect(() => {
    fetchPhotosRef.current = fetchPhotos;
  }, [fetchPhotos]);

  // When the user switches into the Photos tab, refetch so newly uploaded images
  // appear without a manual browser refresh. Skip the initial mount — the gallery
  // init effect already loads photos once.
  const photosTabMounted = useRef(false);
  useEffect(() => {
    if (!photosTabMounted.current) {
      photosTabMounted.current = true;
      return;
    }
    if (activePanel === 'photos') {
      fetchPhotosRef.current();
    }
  }, [activePanel]);

  const handleCollectionFilterChange = useCallback((newFilter) => {
    setCollectionFilter(newFilter);
    fetchPhotos(true, newFilter);
  }, [fetchPhotos]);

  const loadMorePhotos = useCallback(async () => {
    if (loadingMore || !hasMore) return;

    try {
      setLoadingMore(true);
      const nextPage = page + 1;
      const collParam = collectionFilter && collectionFilter !== 'all' ? `&collection=${collectionFilter}` : '';

      const response = await axios.get(
        `${backendUrl}/api/galleries/${galleryId}/photos?limit=${PHOTOS_PER_PAGE}&skip=${nextPage * PHOTOS_PER_PAGE}${collParam}`,
        axiosConfig
      );

      setPhotos(prev => [...prev, ...response.data]);
      setPage(nextPage);
      setHasMore(response.data.length === PHOTOS_PER_PAGE);
    } catch (error) {
      console.error('Error loading more photos:', error);
      toast.error('Failed to load more photos');
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, hasMore, page, galleryId, backendUrl, axiosConfig, collectionFilter]);

  const fetchAllTags = async () => {
    try {
      const response = await axios.get(
        `${backendUrl}/api/search/tags`,
        axiosConfig
      );
      setAllTags(response.data.tags);
      setCategorizedTags(response.data.categorized || {
        facial_attributes: [],
        emotions: [],
        activities: [],
        people_groups: [],
        event_scenes: [],
        event_objects: [],
        atmosphere: [],
        other: []
      });
    } catch (error) {
      console.error('Error fetching tags:', error);
    }
  };

  const fetchAnalytics = async () => {
    if (!galleryId) return;

    try {
      const response = await axios.get(
        `${backendUrl}/api/galleries/${galleryId}/analytics`,
        { ...axiosConfig, params: { tz: viewerTimeZone() } }
      );
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
  };

  const refreshGallery = async () => {
    try {
      const response = await axios.get(
        `${backendUrl}/api/galleries`,
        axiosConfig
      );
      const updatedGallery = response.data.find(g => g.id === galleryId);
      if (updatedGallery) {
        setGallery(updatedGallery);
        checkProcessingStatus(updatedGallery);
      }
    } catch (error) {
      console.error('Error refreshing gallery:', error);
    }
  };

  const handleDeletePhoto = async (photoId) => {
    try {
      await axios.delete(
        `${backendUrl}/api/photos/${photoId}`,
        axiosConfig
      );
      toast.success('Photo deleted');
      await fetchPhotos();
      await fetchAnalytics();
    } catch (error) {
      toast.error('Failed to delete photo');
    }
  };

  const handleSetFeatured = async (photoId) => {
    try {
      await axios.post(
        `${backendUrl}/api/galleries/${galleryId}/set-featured-photo/${photoId}`,
        {},
        axiosConfig
      );
      toast.success('Photo set as featured!');
      await refreshGallery();
    } catch (error) {
      toast.error('Failed to set photo as featured');
    }
  };

  // Render the active panel
  const renderPanel = () => {
    // Safety net: crew may only render their permitted panels.
    const panel = (isCrew && !['photos', 'upload', 'share'].includes(activePanel))
      ? 'photos'
      : activePanel;
    switch (panel) {
      case 'photos':
        return (
          <PhotosPanel
            gallery={gallery}
            photos={photos}
            totalPhotoCount={analytics.total_photos || photos.length}
            loading={loading}
            loadingMore={loadingMore}
            hasMore={hasMore}
            loadMorePhotos={loadMorePhotos}
            onDeletePhoto={handleDeletePhoto}
            onSetFeatured={handleSetFeatured}
            onRefresh={fetchPhotos}
            allTags={allTags}
            categorizedTags={categorizedTags}
            collectionFilter={collectionFilter}
            onCollectionFilterChange={handleCollectionFilterChange}
            axiosConfig={axiosConfig}
            backendUrl={backendUrl}
            galleryId={galleryId}
            isCrew={isCrew}
          />
        );
      case 'upload':
        return (
          <UploadPanel
            gallery={gallery}
            galleryId={galleryId}
            photos={photos}
            onUploadComplete={fetchPhotos}
            axiosConfig={axiosConfig}
            backendUrl={backendUrl}
          />
        );
      case 'collections':
        return (
          <CollectionsPanel
            gallery={gallery}
            galleryId={galleryId}
            axiosConfig={axiosConfig}
            backendUrl={backendUrl}
            onRefreshGallery={refreshGallery}
          />
        );
      case 'headshots':
        return (
          <HeadshotDeliveriesPanel
            gallery={gallery}
            galleryId={galleryId}
            photos={photos}
            axiosConfig={axiosConfig}
            backendUrl={backendUrl}
          />
        );
      case 'frames':
        return (
          <FramesPanel
            gallery={gallery}
            galleryId={galleryId}
            photos={photos}
            axiosConfig={axiosConfig}
            backendUrl={backendUrl}
            onRefreshGallery={refreshGallery}
          />
        );
      case 'share':
        return (
          <ShareLinksPanel
            gallery={gallery}
            galleryId={galleryId}
            axiosConfig={axiosConfig}
            backendUrl={backendUrl}
            isCrew={isCrew}
            onRefreshGallery={refreshGallery}
          />
        );
      case 'deliveries':
        return (
          <DeliveriesPanel
            gallery={gallery}
            galleryId={galleryId}
            axiosConfig={axiosConfig}
            backendUrl={backendUrl}
          />
        );
      case 'analytics':
        return (
          <AnalyticsPanel
            gallery={gallery}
            galleryId={galleryId}
            analytics={analytics}
            onRefresh={fetchAnalytics}
            axiosConfig={axiosConfig}
            backendUrl={backendUrl}
          />
        );
      case 'settings':
        return (
          <SettingsPanel
            gallery={gallery}
            galleryId={galleryId}
            photos={photos}
            analytics={analytics}
            processingStatus={processingStatus}
            onRefreshGallery={refreshGallery}
            axiosConfig={axiosConfig}
            backendUrl={backendUrl}
          />
        );
      default:
        return null;
    }
  };

  if (!gallery) {
    return (
      <div className="flex items-center justify-center h-screen" style={{ background: '#EDEDF0' }}>
        <div className="animate-spin rounded-full h-12 w-12" style={{ border: '3px solid rgba(191,33,47,0.2)', borderTopColor: '#BF212F' }}></div>
      </div>
    );
  }

  return (
    <div className="flex h-screen overflow-hidden font-tac-body" style={{ background: '#EDEDF0' }}>
      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar - Hidden on mobile unless menu open */}
      <div className={`
        fixed md:relative inset-y-0 left-0 z-50
        transform transition-transform duration-300 ease-in-out md:transform-none
        ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
      `}>
        <GallerySidebar
          activePanel={activePanel}
          onPanelChange={handlePanelChange}
          collapsed={sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
          gallery={gallery}
          photoCount={analytics.total_photos || photos.length}
          onClose={() => setMobileMenuOpen(false)}
          isMobile={mobileMenuOpen}
          userRole={userRole}
        />
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <GalleryHeader
          gallery={gallery}
          onBack={() => navigate('/')}
          activePanel={activePanel}
          onMenuToggle={() => setMobileMenuOpen(!mobileMenuOpen)}
          mobileMenuOpen={mobileMenuOpen}
        />

        {/* Panel Content */}
        <main className="flex-1 overflow-auto">
          {renderPanel()}
        </main>
      </div>

      {/* Floating Upload Progress */}
      <FloatingUploadProgress />
    </div>
  );
};

export default GalleryAdmin;
